<template>
    <AdminLayout :crumbs="crumbs" :season_id="season_id">
        <template v-if="season">
            <EventHeader :season="season" />

            <div class="a-card mb-4">
                <div class="a-card-header">
                    <h2 class="a-section-title">Datos del {{ season_label.toLowerCase() }}</h2>
                </div>
                <div class="a-card-body" v-if="draft">
                    <SeasonFields :model="draft" />
                </div>
                <div class="settings-foot" v-if="is_admin">
                    <span class="a-subtle" v-if="dirty">Hay cambios sin guardar</span>
                    <button type="button" class="btn btn-secondary" :disabled="!dirty" @click="reset">Descartar</button>
                    <button type="button" class="btn btn-primary" :disabled="!dirty" @click="save">Guardar cambios</button>
                </div>
            </div>

            <!-- Event look for players and the big screen (colours, logo,
                 background stripes) - see shared/event-branding.js. Saved with
                 the rest of the event data above. -->
            <div class="a-card mb-4" v-if="draft">
                <div class="a-card-header">
                    <div>
                        <h2 class="a-section-title">Identidad visual</h2>
                        <div class="a-subtle">Colores, logo y fondo de las pantallas de juego de este {{ season_label.toLowerCase() }}. El panel no cambia.</div>
                    </div>
                </div>
                <div class="a-card-body branding-body">
                    <EventBrandingEditor v-model="draft.branding" :name="draft.name" :theme="draft.theme" />
                </div>
                <div class="settings-foot" v-if="is_admin">
                    <span class="a-subtle" v-if="dirty">Hay cambios sin guardar</span>
                    <button type="button" class="btn btn-secondary" :disabled="!dirty" @click="reset">Descartar</button>
                    <button type="button" class="btn btn-primary" :disabled="!dirty" @click="save">Guardar cambios</button>
                </div>
            </div>

            <div class="a-card mb-4">
                <div class="a-card-header">
                    <div>
                        <h2 class="a-section-title">Maillot 3D</h2>
                        <div class="a-subtle">El modelo que ven los equipos al elegir su color. Sin modelo propio se usa el de la app.</div>
                    </div>
                </div>
                <div class="a-card-body d-flex align-items-center gap-3 flex-wrap">
                    <EventModelThumbnail :model-url="season.jersey_model_url" :label="'Modelo de ' + season.name" />
                    <div class="flex-fill">
                        <div v-if="season.jersey_model_url" class="mb-2"><span class="a-tag"><i class="bi bi-box"></i>{{ season.jersey_model_url.split('/').pop() }}</span></div>
                        <div v-else class="a-muted mb-2">Usando el modelo por defecto.</div>
                        <div class="d-flex gap-2" v-if="is_admin">
                            <label class="btn btn-secondary btn-sm mb-0">
                                <i class="bi bi-upload"></i>{{ season.jersey_model_url ? 'Cambiar modelo' : 'Subir modelo' }}
                                <input type="file" accept=".glb,.obj" class="d-none" @change="upload_model">
                            </label>
                            <button v-if="season.jersey_model_url" type="button" class="btn btn-ghost btn-sm" @click="send('REMOVE_SEASON_MODEL', { season_id: season.id })">Volver al de la app</button>
                        </div>
                        <div class="form-text">Formatos .glb u .obj (máx. 10 MB).</div>
                    </div>
                </div>
            </div>

            <div class="a-card danger" v-if="is_admin">
                <div class="a-card-body d-flex align-items-center gap-3 flex-wrap">
                    <div class="flex-fill">
                        <h2 class="a-section-title">Eliminar {{ season_label.toLowerCase() }}</h2>
                        <div class="a-subtle">Se borran también sus etapas, rankings e inscripciones. No se puede deshacer.</div>
                    </div>
                    <button type="button" class="btn btn-outline-danger" @click="delete_modal.show()">Eliminar…</button>
                </div>
            </div>
        </template>

        <div class="modal fade a-modal" ref="deleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="season">
                    <div class="modal-header">
                        <h1 class="modal-title">Eliminar {{ season.name }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <p>Para confirmar, escribe el nombre del {{ season_label.toLowerCase() }}:</p>
                        <input type="text" class="form-control" v-model="confirm_name" :placeholder="season.name">
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" :disabled="confirm_name.trim() != season.name" @click="remove">Eliminar definitivamente</button>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script>
import { Modal } from 'bootstrap'
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import EventHeader from '@/components/admin/EventHeader.vue'
import SeasonFields from '@/components/admin/SeasonFields.vue'
import EventModelThumbnail from '@/components/EventModelThumbnail.vue'
import EventBrandingEditor from '@/components/EventBrandingEditor.vue'
import { normalizeBranding } from '@/utils/event-branding'
import { to_datetime_local, with_iso_dates } from '@/utils/admin-format'

// Everything about one event that isn't its content: name, dates, scoring,
// theme, jersey model - and deleting it.
export default {
    name: 'EventSettings',
    components: { AdminLayout, EventHeader, SeasonFields, EventModelThumbnail, EventBrandingEditor },
    data(){
        return { draft: null, confirm_name: '' };
    },
    computed: {
        season_id(){ return parseInt(this.$route.params.season_id, 10); },
        season(){ return (this.$store.state.game.seasons || []).find((s) => s.id == this.season_id) || null; },
        season_label(){ return utils.get_season_label(window.MODE); },
        crumbs(){
            return [
                { label: utils.get_season_label(window.MODE, true), to: { name: 'Panel' } },
                { label: this.season ? this.season.name : '…', to: { name: 'Stages', params: { season_id: this.season_id } } },
                { label: 'Configuración' },
            ];
        },
        is_admin(){
            const user = this.$store.state.game.user;
            return !!user && user.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        original(){ return this.season ? this.to_draft(this.season) : null; },
        dirty(){
            // Inputs hand back strings ("10") where the server stores numbers.
            const norm = (d) => JSON.stringify(Object.assign({}, d, { base_score: Number(d.base_score), branding: normalizeBranding(d.branding) }));
            return !!this.draft && !!this.original && norm(this.draft) != norm(this.original);
        }
    },
    watch: {
        // Start from the live season (and again when switching events);
        // never overwrite edits in progress with a broadcast.
        season: { immediate: true, handler(season, old){
            if(season && (!this.draft || !old || old.id != season.id)) this.reset();
        } },
    },
    mounted(){
        this.delete_modal = new Modal(this.$refs.deleteModal);
    },
    beforeUnmount(){
        this.delete_modal.dispose();
    },
    methods: {
        to_draft(s){
            return {
                id: s.id,
                name: s.name,
                pre_start_date: to_datetime_local(s.pre_start_date),
                start_date: to_datetime_local(s.start_date),
                end_date: to_datetime_local(s.end_date),
                base_score: s.base_score,
                theme: s.theme || utils.DEFAULT_THEME,
                branding: normalizeBranding(s.branding),
            };
        },
        reset(){
            this.draft = this.to_draft(this.season);
        },
        save(){
            if(!utils.is_valid_parameter(this.draft.name)){
                window.toast.error('El nombre no puede quedar vacío.');
                return;
            }
            this.send('EDIT_SEASON', with_iso_dates(this.draft));
            // The broadcast brings the saved values back; align the baseline now.
            this.draft = JSON.parse(JSON.stringify(this.draft));
        },
        upload_model(e){
            const file = e.target.files[0];
            e.target.value = '';
            if(!file) return;
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if(ext != 'glb' && ext != 'obj'){
                window.toast.error('Sube un archivo .glb u .obj.');
                return;
            }
            const reader = new FileReader();
            reader.onload = (evt) => this.send('UPLOAD_SEASON_MODEL', { season_id: this.season.id, filename: file.name, data: evt.target.result });
            reader.readAsDataURL(file);
        },
        remove(){
            this.send('REMOVE_SEASON', this.season.id);
            this.delete_modal.hide();
            this.$router.push({ name: 'Panel' });
        },
        send(rpc, params){
            window.network.wso.send(JSON.stringify({ action: window.RPC[rpc], params }));
        }
    }
}
</script>

<style scoped>
.settings-foot{
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid var(--a-line);
    background: var(--a-surface-2);
    border-radius: 0 0 var(--a-radius) var(--a-radius);
}
.danger{ border-color: #f2c4c5; }
/* The editor brings its own heading/separator for the old modal layout. */
.branding-body :deep(.branding-editor){ border-top: 0; padding-top: 0; margin-bottom: 0 !important; }
.branding-body :deep(.branding-editor > h2){ display: none; }
</style>
